import {
  forwardRef,
  useId,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';
import clsx from 'clsx';
import { ChevronRight, Info, Loader2, Lock, TriangleAlert } from 'lucide-react';

/* ------------------------------------------------------------------ button --- */

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'subtle';
type ButtonSize = 'sm' | 'md' | 'lg';

const buttonBase =
  'inline-flex items-center justify-center gap-2 font-medium rounded-control border select-none ' +
  'whitespace-nowrap transition-colors duration-150 ease-out ' +
  'active:brightness-95 disabled:opacity-50 disabled:cursor-not-allowed disabled:active:brightness-100';

const buttonVariants: Record<ButtonVariant, string> = {
  primary:
    'bg-brand text-brand-ink border-brand hover:bg-brand-strong hover:border-brand-strong',
  secondary:
    'bg-surface text-ink border-control-line hover:bg-surface-2 hover:border-ink-subtle',
  ghost: 'bg-transparent text-ink border-transparent hover:bg-surface-2',
  danger: 'bg-danger-wash text-danger border-danger/50 hover:bg-danger hover:text-surface hover:border-danger',
  subtle: 'bg-surface-2 text-ink border-line hover:bg-surface-3',
};

const buttonSizes: Record<ButtonSize, string> = {
  sm: 'text-sm px-2.5 py-1.5',
  md: 'text-sm px-3.5 py-2',
  lg: 'text-base px-5 py-2.5',
};

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  icon?: ReactNode;
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'secondary', size = 'md', loading, icon, className, children, disabled, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      className={clsx(buttonBase, buttonVariants[variant], buttonSizes[size], className)}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading ? <Loader2 size={16} className="animate-spin" aria-hidden /> : icon}
      {children}
    </button>
  );
});

export const IconButton = forwardRef<
  HTMLButtonElement,
  ButtonHTMLAttributes<HTMLButtonElement> & { label: string; variant?: ButtonVariant }
>(function IconButton({ label, variant = 'ghost', className, children, ...rest }, ref) {
  return (
    <button
      ref={ref}
      aria-label={label}
      title={label}
      className={clsx(
        buttonBase,
        buttonVariants[variant],
        'h-8 w-8 p-0 rounded-control',
        className,
      )}
      {...rest}
    >
      {children}
    </button>
  );
});

/* -------------------------------------------------------------------- card --- */

export function Card({
  className,
  children,
  as: As = 'div',
  ...rest
}: {
  className?: string;
  children: ReactNode;
  as?: 'div' | 'section' | 'article' | 'li';
} & React.HTMLAttributes<HTMLElement>) {
  return (
    <As
      className={clsx(
        'bg-surface border border-line rounded-card shadow-card',
        className,
      )}
      {...rest}
    >
      {children}
    </As>
  );
}

export function CardHeader({
  title,
  description,
  actions,
  icon,
  className,
}: {
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  icon?: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={clsx(
        'flex items-start justify-between gap-4 px-5 py-4 border-b border-line',
        className,
      )}
    >
      <div className="flex items-start gap-3 min-w-0">
        {icon ? <span className="mt-0.5 text-ink-muted shrink-0">{icon}</span> : null}
        <div className="min-w-0">
          <h2 className="font-semibold text-ink leading-tight">{title}</h2>
          {description ? (
            <p className="text-sm text-ink-muted mt-1 leading-relaxed">{description}</p>
          ) : null}
        </div>
      </div>
      {actions ? <div className="flex items-center gap-2 shrink-0">{actions}</div> : null}
    </div>
  );
}

/* ------------------------------------------------------------------- badge --- */

type Tone = 'neutral' | 'brand' | 'accent' | 'warn' | 'danger' | 'ok' | 'project' | 'ai';

const badgeTones: Record<Tone, string> = {
  neutral: 'bg-surface-2 text-ink-muted border-line',
  brand: 'bg-brand-wash text-brand-text border-brand-text/30',
  accent: 'bg-accent-wash text-accent border-accent/30',
  warn: 'bg-warn-wash text-warn border-warn/30',
  danger: 'bg-danger-wash text-danger border-danger/30',
  ok: 'bg-accent-wash text-ok border-ok/30',
  /* The capstone: structure, not a status. */
  project: 'bg-project-wash text-project border-project/30',
  /* Reserved for the model. */
  ai: 'bg-ai-wash text-ai border-ai/30',
};

export function Badge({
  tone = 'neutral',
  children,
  className,
  icon,
}: {
  tone?: Tone;
  children: ReactNode;
  className?: string;
  icon?: ReactNode;
}) {
  return (
    <span
      className={clsx(
        'inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-control border',
        badgeTones[tone],
        className,
      )}
    >
      {icon}
      {children}
    </span>
  );
}

/** Status chip for draft/published/archived, so it reads the same everywhere. */
export function StatusBadge({
  status,
  hasUnpublishedChanges,
}: {
  status: 'draft' | 'published' | 'archived';
  hasUnpublishedChanges?: boolean;
}) {
  if (status === 'published' && hasUnpublishedChanges) {
    return <Badge tone="warn">Published · unpublished changes</Badge>;
  }
  if (status === 'published') return <Badge tone="ok">Published</Badge>;
  if (status === 'archived') return <Badge tone="neutral">Archived</Badge>;
  return <Badge tone="warn">Draft</Badge>;
}

/* -------------------------------------------------------------------- form --- */

export function Field({
  label,
  hint,
  error,
  required,
  children,
  htmlFor,
  className,
}: {
  label: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  required?: boolean;
  children: ReactNode;
  htmlFor?: string;
  className?: string;
}) {
  return (
    <div className={clsx('flex flex-col gap-1.5', className)}>
      <label htmlFor={htmlFor} className="text-sm font-medium text-ink">
        {label}
        {required ? (
          <span className="text-danger ml-0.5" aria-hidden>
            *
          </span>
        ) : null}
      </label>
      {children}
      {/* Reserved whether or not there is anything in it: an error appearing
          under a field must not push the rest of the form down. */}
      <p
        className={clsx('min-h-[1lh] text-xs', error ? 'text-danger' : 'text-ink-muted')}
        aria-live="polite"
      >
        {error ?? hint ?? ''}
      </p>
    </div>
  );
}

const controlBase =
  'w-full bg-surface border border-control-line rounded-control px-3 py-2 text-sm text-ink ' +
  'placeholder:text-ink-subtle disabled:opacity-60 disabled:cursor-not-allowed ' +
  'transition-colors duration-150 ease-out hover:border-ink-subtle';

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(
  function Input({ className, ...rest }, ref) {
    return <input ref={ref} className={clsx(controlBase, className)} {...rest} />;
  },
);

export const Textarea = forwardRef<
  HTMLTextAreaElement,
  TextareaHTMLAttributes<HTMLTextAreaElement>
>(function Textarea({ className, ...rest }, ref) {
  return (
    <textarea
      ref={ref}
      className={clsx(controlBase, 'min-h-24 resize-y leading-relaxed', className)}
      {...rest}
    />
  );
});

export const Select = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement>>(
  function Select({ className, children, ...rest }, ref) {
    return (
      <select ref={ref} className={clsx(controlBase, 'pr-8', className)} {...rest}>
        {children}
      </select>
    );
  },
);

export function Checkbox({
  label,
  description,
  className,
  ...rest
}: InputHTMLAttributes<HTMLInputElement> & { label: ReactNode; description?: ReactNode }) {
  const id = useId();
  return (
    <div className={clsx('flex items-start gap-2.5', className)}>
      <input
        id={id}
        type="checkbox"
        className="mt-0.5 h-4 w-4 rounded-[0.25rem] border-control-line shrink-0"
        {...rest}
      />
      <label htmlFor={id} className="text-sm text-ink leading-snug">
        {label}
        {description ? (
          <span className="block text-xs text-ink-subtle mt-0.5">{description}</span>
        ) : null}
      </label>
    </div>
  );
}

/* ------------------------------------------------------------------ status --- */

export function Spinner({ className, label = 'Loading' }: { className?: string; label?: string }) {
  return (
    <span role="status" aria-label={label} className={clsx('inline-flex', className)}>
      <Loader2 size={18} className="animate-spin text-ink-muted" aria-hidden />
    </span>
  );
}

export function Skeleton({ className }: { className?: string }) {
  return (
    <div
      aria-hidden
      className={clsx('animate-pulse bg-surface-2 rounded-control', className)}
    />
  );
}

export function LoadingPane({ label = 'Loading…' }: { label?: string }) {
  return (
    <div className="flex items-center justify-center gap-3 py-16 text-sm text-ink-muted">
      <Spinner />
      {label}
    </div>
  );
}

export function EmptyState({
  title,
  description,
  action,
  icon,
  className,
}: {
  title: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
  icon?: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={clsx(
        'flex flex-col items-center text-center gap-3 py-14 px-6 rounded-card',
        'border border-dashed border-line bg-surface/50',
        className,
      )}
    >
      {icon ? <div className="text-ink-subtle">{icon}</div> : null}
      <h3 className="font-semibold text-ink">{title}</h3>
      {description ? (
        <p className="text-sm text-ink-muted max-w-prose leading-relaxed">{description}</p>
      ) : null}
      {action ? <div className="mt-1">{action}</div> : null}
    </div>
  );
}

const alertTones: Record<'info' | 'warn' | 'danger' | 'ok', string> = {
  info: 'bg-project-wash border-project/25 text-ink',
  warn: 'bg-warn-wash border-warn/40 text-ink',
  danger: 'bg-danger-wash border-danger/40 text-ink',
  ok: 'bg-accent-wash border-accent/40 text-ink',
};

export function Alert({
  tone = 'info',
  title,
  children,
  className,
  icon,
}: {
  tone?: 'info' | 'warn' | 'danger' | 'ok';
  title?: ReactNode;
  children?: ReactNode;
  className?: string;
  icon?: ReactNode;
}) {
  const defaultIcon =
    tone === 'warn' || tone === 'danger' ? <TriangleAlert size={16} /> : <Info size={16} />;
  return (
    <div
      className={clsx('flex gap-3 rounded-control border px-4 py-3 text-sm', alertTones[tone], className)}
      role={tone === 'danger' ? 'alert' : undefined}
    >
      <span className="mt-0.5 shrink-0 text-ink-muted" aria-hidden>
        {icon ?? defaultIcon}
      </span>
      <div className="min-w-0">
        {title ? <p className="font-semibold">{title}</p> : null}
        {children ? <div className={clsx(title && 'mt-1', 'leading-relaxed')}>{children}</div> : null}
      </div>
    </div>
  );
}

/**
 * A control the user cannot use, shown rather than hidden, with the server's
 * own reason attached. §7 of the spec: visibly locked with a reason beats
 * silently missing.
 */
export function LockedAction({
  reason,
  children,
  className,
}: {
  reason: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <span
      className={clsx('inline-flex items-center gap-2', className)}
      title={reason}
    >
      <span className="inline-flex items-center gap-1.5 text-sm text-ink-subtle border border-line rounded-control px-3 py-2 bg-surface-2 cursor-not-allowed">
        <Lock size={14} aria-hidden />
        {children}
      </span>
      <span className="sr-only">{reason}</span>
    </span>
  );
}

/* ---------------------------------------------------------------- progress --- */

export function ProgressBar({
  value,
  label,
  tone = 'brand',
  className,
  showValue = false,
}: {
  value: number;
  label?: string;
  tone?: 'brand' | 'accent';
  className?: string;
  showValue?: boolean;
}) {
  const clamped = Math.max(0, Math.min(100, value));
  return (
    <div className={clsx('flex items-center gap-2', className)}>
      <div
        className="h-1.5 flex-1 rounded-full bg-surface-3 overflow-hidden"
        role="progressbar"
        aria-valuenow={Math.round(clamped)}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={label ?? 'Progress'}
      >
        {/* scaleX, not width: width animates on the main thread and gate 14
            of the house checklist bans it. The origin is the inline start so
            the bar grows from the left in both writing directions. */}
        <div
          className={clsx(
            'h-full w-full origin-left rounded-full transition-transform duration-500 ease-out',
            tone === 'brand' ? 'bg-brand-text' : 'bg-accent',
          )}
          style={{ transform: `scaleX(${clamped / 100})` }}
        />
      </div>
      {showValue ? (
        <span className="font-mono text-xs text-ink-muted tabular-nums w-9 text-right">
          {Math.round(clamped)}%
        </span>
      ) : null}
    </div>
  );
}

export function ProgressRing({
  value,
  size = 44,
  stroke = 4,
  label,
}: {
  value: number;
  size?: number;
  stroke?: number;
  label?: string;
}) {
  const clamped = Math.max(0, Math.min(100, value));
  const r = (size - stroke) / 2;
  const circumference = 2 * Math.PI * r;
  const offset = circumference * (1 - clamped / 100);
  return (
    <div
      className="relative inline-flex items-center justify-center shrink-0"
      style={{ width: size, height: size }}
      role="img"
      aria-label={label ?? `${Math.round(clamped)}% complete`}
    >
      <svg width={size} height={size} className="-rotate-90" aria-hidden>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          strokeWidth={stroke}
          className="stroke-surface-3"
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          className="stroke-brand-text transition-[stroke-dashoffset] duration-500 ease-out"
        />
      </svg>
      <span className="absolute font-mono text-[0.65rem] font-semibold tabular-nums text-ink">
        {Math.round(clamped)}
      </span>
    </div>
  );
}

/** The ●●●○○ milestone indicator from the capstone hero band. */
export function DotProgress({ done, total }: { done: number; total: number }) {
  if (total <= 0) return null;
  return (
    <span className="inline-flex items-center gap-1.5" aria-label={`${done} of ${total} complete`}>
      <span className="flex gap-1" aria-hidden>
        {Array.from({ length: total }, (_, i) => (
          <span
            key={i}
            className={clsx(
              'h-2 w-2 rounded-full',
              i < done ? 'bg-accent' : 'bg-surface-3 border border-line',
            )}
          />
        ))}
      </span>
      <span className="font-mono text-xs text-ink-muted tabular-nums">
        {done}/{total}
      </span>
    </span>
  );
}

/* ------------------------------------------------------------------- misc --- */

export function SectionHeading({
  children,
  id,
  action,
  className,
}: {
  children: ReactNode;
  id?: string;
  action?: ReactNode;
  className?: string;
}) {
  return (
    <div className={clsx('flex items-baseline justify-between gap-4 mb-3', className)}>
      <h2 id={id} className="text-lg font-semibold tracking-tight text-ink wrap-anywhere">
        {children}
      </h2>
      {action}
    </div>
  );
}

export function Divider({ className }: { className?: string }) {
  return <hr className={clsx('border-0 border-t border-line', className)} />;
}

export function Avatar({ name, size = 32 }: { name: string; size?: number }) {
  const text = name
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0])
    .join('')
    .toUpperCase();
  return (
    <span
      className="inline-flex items-center justify-center rounded-full bg-project-wash text-project font-semibold shrink-0"
      style={{ width: size, height: size, fontSize: size * 0.38 }}
      aria-hidden
    >
      {text || '?'}
    </span>
  );
}

/**
 * Progressive disclosure built on <details>, so the hidden text stays
 * findable by the browser's own find-in-page and reachable by screen readers
 * instead of being display:none'd out of existence.
 */
export function Disclosure({
  summary,
  children,
  defaultOpen = false,
  tone = 'neutral',
  className,
  onOpen,
  id,
}: {
  summary: ReactNode;
  children: ReactNode;
  defaultOpen?: boolean;
  tone?: 'neutral' | 'warn';
  className?: string;
  onOpen?: () => void;
  id?: string;
}) {
  return (
    <details
      id={id}
      open={defaultOpen}
      className={clsx(
        'group rounded-control border bg-surface',
        tone === 'warn' ? 'border-warn/40' : 'border-line',
        className,
      )}
      onToggle={(e) => {
        if ((e.currentTarget as HTMLDetailsElement).open) onOpen?.();
      }}
    >
      <summary className="flex items-center justify-between gap-3 px-4 py-3 text-sm font-medium text-ink hover:bg-surface-2 rounded-control">
        {summary}
        <ChevronRight
          size={16}
          aria-hidden
          className="text-ink-subtle transition-transform duration-200 ease-out group-open:rotate-90 shrink-0"
        />
      </summary>
      <div className="px-4 pb-4 pt-1 border-t border-line">{children}</div>
    </details>
  );
}

export function Tabs<T extends string>({
  tabs,
  active,
  onChange,
  className,
  idPrefix,
  label,
  variant = 'underline',
}: {
  /** `title` is the hover text — the full name when `label` is shortened. */
  tabs: { id: T; label: ReactNode; badge?: ReactNode; title?: string }[];
  active: T;
  onChange: (id: T) => void;
  className?: string;
  /**
   * When given, each tab gets the id `${idPrefix}-tab-${id}` and points at a
   * panel with the id `${idPrefix}-panel-${id}`, which the caller renders.
   */
  idPrefix?: string;
  label?: string;
  /** `pill` is the smaller style for a row of tabs nested inside another tab. */
  variant?: 'underline' | 'pill';
}) {
  const pill = variant === 'pill';
  return (
    <div
      role="tablist"
      aria-label={label}
      className={clsx(
        'flex gap-1 overflow-x-auto',
        pill ? 'flex-wrap' : 'border-b border-line',
        className,
      )}
    >
      {tabs.map((tab) => (
        <button
          key={tab.id}
          role="tab"
          type="button"
          id={idPrefix ? `${idPrefix}-tab-${tab.id}` : undefined}
          aria-controls={idPrefix ? `${idPrefix}-panel-${tab.id}` : undefined}
          aria-selected={active === tab.id}
          title={tab.title}
          onClick={() => onChange(tab.id)}
          className={clsx(
            'text-sm whitespace-nowrap flex items-center gap-2',
            'transition-colors duration-150 ease-out',
            pill
              ? clsx(
                  'px-3 py-1.5 rounded-full border',
                  active === tab.id
                    ? 'border-brand bg-brand-wash text-ink font-semibold'
                    : 'border-line text-ink-muted font-medium hover:text-ink hover:border-line-strong',
                )
              : clsx(
                  'px-3.5 py-2 border-b-2 -mb-px',
                  active === tab.id
                    ? 'border-brand text-ink font-semibold'
                    : 'border-transparent text-ink-muted font-medium hover:text-ink hover:border-line-strong',
                ),
          )}
        >
          {tab.label}
          {tab.badge}
        </button>
      ))}
    </div>
  );
}

export { clsx };
